"use client";

import { useEffect } from "react";

/**
 * Adds `.is-visible` to elements with `.reveal` or `.stagger`
 * when they enter the viewport. Uses IntersectionObserver for
 * broad browser support. No-op when prefers-reduced-motion is set.
 *
 * Mount once in the root layout — it observes the entire document.
 */
export function ScrollReveal() {
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const revealAll = () => {
      document.querySelectorAll(".reveal, .stagger").forEach((el) => {
        el.classList.add("is-visible");
      });
    };

    if (prefersReduced || !("IntersectionObserver" in window)) {
      revealAll();
      return;
    }

    const observed = new WeakSet<Element>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    const observeRevealTargets = () => {
      document.querySelectorAll(".reveal, .stagger").forEach((el) => {
        if (observed.has(el) || el.classList.contains("is-visible")) return;
        observed.add(el);
        observer.observe(el);
      });
    };

    observeRevealTargets();

    const mutationObserver = new MutationObserver(() => {
      observeRevealTargets();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    const safetyTimer = window.setTimeout(() => {
      revealAll();
    }, 1800);

    return () => {
      window.clearTimeout(safetyTimer);
      mutationObserver.disconnect();
      observer.disconnect();
    };
  }, []);

  return null;
}
